// react
import React, { useState, useEffect } from "react";

// context
import { UserAuth } from "context/AuthContext";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import { Grid, Card, CardContent, Typography, CircularProgress } from "@mui/material";

// ui-components
import ActivatedPubKeyTable from "ui-components/reseller/ActivatedPubKeyTable";
import ActivatedAPIKeyTable from "ui-components/reseller/ActivatedAPIKeyTable";
import ActivateAPIKey from "ui-components/reseller/ActivateAPIKey";
import ActivatePublicKey from "ui-components/reseller/ActivatePublicKey";
import { EnabledPublicKeySuccessful, EnabledPublicKeyError, EnabledAPIKeySuccessful, EnabledAPIKeyError } from "ui-components/ORFeedbacks";

// firebase
import { db } from "config/firebase";
import { doc, collection, query, where, onSnapshot } from "firebase/firestore";

// SafeTwin
import { genRndStringPrintable } from "SafeTwin/crypto/cryptolibsodium";

// user-controller
import getActivatedPublicKeys from "user-controller/getActivatedPublicKeys";
import getActivatedAPIKeys from "user-controller/getActivatedAPIKeys";

// A ---------------------------------------------------------------------- M

const ResellerPanel = () => {
  const { user } = UserAuth();
  const { t } = useTranslation();

  const [publicKeysData, setPublicKeysData] = useState([]);
  const [APIKey, setAPIKey] = useState("");
  const [APIKeysData, setAPIKeysData] = useState([]);
  const [isPublicKeyDialogOpen, setPublicKeyDialogOpen] = useState(false);
  const [isApiKeyDialogOpen, setApiKeyDialogOpen] = useState(false);
  const [openPubKeySnackbarSuccessful, setOpenPubKeySnackbarSuccessful] = useState(false);
  const [openPubKeySnackbarError, setOpenPubKeySnackbarError] = useState(false);
  const [openAPIKeySnackbarSuccessful, setOpenAPIKeySnackbarSuccessful] = useState(false);
  const [openAPIKeySnackbarError, setOpenAPIKeySnackbarError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const handleOpenPublicKeyDialog = () => {
    setPublicKeyDialogOpen(true);
  };

  const handleClosePublicKeyDialog = () => {
    setPublicKeyDialogOpen(false);
  };

  const handleOpenApiKeyDialog = () => {
    const APIKey = genRndStringPrintable(32);
    setAPIKey(APIKey);
    setApiKeyDialogOpen(true);
  };

  const handleCloseApiKeyDialog = () => {
    setApiKeyDialogOpen(false);
  };

  useEffect(() => {
    const fetchActivatedPubKeys = async () => {
      const activatedPublicKeys = await getActivatedPublicKeys(user.uid);
      activatedPublicKeys.sort((pk1, pk2) => pk2.added_on - pk1.added_on);
      setPublicKeysData(activatedPublicKeys);
    };

    const fetchActivatedAPIKeys = async () => {
      const activatedAPIKeys = await getActivatedAPIKeys(user.uid);
      activatedAPIKeys.sort((ak1, ak2) => ak2.added_on - ak1.added_on);
      setAPIKeysData(activatedAPIKeys);
    };

    const fetchData = async () => {
      await fetchActivatedPubKeys();
      await fetchActivatedAPIKeys();
      setIsLoading(false);
    };

    fetchData();

    const currentTimestampInSeconds = Math.floor(Date.now() / 1000);

    const userRef = doc(db, "enabled_records", user.uid);
    const enabledPublicKeysCollectionRef = collection(userRef, "enabled_public_keys");
    const enabledAPIKeysCollectionRef = collection(userRef, "enabled_api_keys");

    const newAdditionsQueryEnabledPublicKeys = query(enabledPublicKeysCollectionRef, where("added_on", ">=", currentTimestampInSeconds.toString()));
    const newAdditionsQueryEnabledAPIKeys = query(enabledAPIKeysCollectionRef, where("added_on", ">=", currentTimestampInSeconds.toString()));

    const newAdditionsUnsubscribeEnabledPublicKeys = onSnapshot(newAdditionsQueryEnabledPublicKeys, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === "added") fetchActivatedPubKeys();
      });
    });

    const newAdditionsUnsubscribeEnabledAPIKeys = onSnapshot(newAdditionsQueryEnabledAPIKeys, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === "added") fetchActivatedAPIKeys();
      });
    });

    return () => {
      newAdditionsUnsubscribeEnabledPublicKeys();
      newAdditionsUnsubscribeEnabledAPIKeys();
    };
  }, [user]);

  if (isLoading) {
    return (
      <Grid container justifyContent="center" mt="30%">
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <>
      <Grid item container xs={12} spacing={2}>
        <Grid item xs={12} sm={10}>
          <Typography variant="h6" fontWeight="bold">
            {t("reseller_panel")}
          </Typography>
        </Grid>

        <Grid item container spacing={3}>
          {APIKeysData && (
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ borderRadius: 1, p: 1 }}>
                <CardContent>
                  <ActivatedAPIKeyTable APIKeysData={APIKeysData} handleOpen={handleOpenApiKeyDialog} />
                </CardContent>
              </Card>
            </Grid>
          )}
          {publicKeysData && (
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ borderRadius: 1, p: 1 }}>
                <CardContent>
                  <ActivatedPubKeyTable publicKeysData={publicKeysData} handleOpen={handleOpenPublicKeyDialog} />
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Grid>

      {APIKey && (
        <ActivateAPIKey
          APIKey={APIKey}
          open={isApiKeyDialogOpen}
          setOpen={setApiKeyDialogOpen}
          handleClose={handleCloseApiKeyDialog}
          setOpenAPIKeySnackbarSuccessful={setOpenAPIKeySnackbarSuccessful}
          setOpenAPIKeySnackbarError={setOpenAPIKeySnackbarError}
        />
      )}

      <ActivatePublicKey
        open={isPublicKeyDialogOpen}
        setOpen={setPublicKeyDialogOpen}
        handleClose={handleClosePublicKeyDialog}
        setOpenPubKeySnackbarSuccessful={setOpenPubKeySnackbarSuccessful}
        setOpenPubKeySnackbarError={setOpenPubKeySnackbarError}
      />

      <EnabledPublicKeySuccessful open={openPubKeySnackbarSuccessful} setOpen={setOpenPubKeySnackbarSuccessful} />
      <EnabledPublicKeyError open={openPubKeySnackbarError} setOpen={setOpenPubKeySnackbarError} />
      <EnabledAPIKeySuccessful open={openAPIKeySnackbarSuccessful} setOpen={setOpenAPIKeySnackbarSuccessful} />
      <EnabledAPIKeyError open={openAPIKeySnackbarError} setOpen={setOpenAPIKeySnackbarError} />
    </>
  );
};

export default ResellerPanel;
